import React from 'react';
import Box from '@material-ui/core/Box';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { Link } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import { getAlgoliaLocalizedField } from 'utils/localeUtils';
import Typography from '@material-ui/core/Typography';
import { getImageFile, fallbackImage } from 'utils/stringUtils';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import LabelValue from 'components/LabelValue';
import Mixpanel from 'utils/mixpanelService';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import useDateLocale from 'components/useDateLocale';
import BlogIcon from 'components/Resources/images/Blue/Blog.svg';
import AuthorSubtitle from './AuthorSubtitle';
import useAlterSubtitleHeight from '../hooks/subtitleContainerHeight';

const styles = theme => ({
  lazyImage: {
    display: 'block',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    objectFit: 'cover',
  },
  date: {
    ...theme.typography.pxSmall,
    minWidth: 67,
  },
  author: {
    ...theme.typography.pxSmall,
    padding: '5px 0',
    display: 'flex',
    maxWidth: 'calc(100% - 82px)',
    '& a': {
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      color: `${theme.palette.text.secondary} !important`,
    },
  },
  typeLabel: {
    display: 'block',
  },
  dateAndAuthorWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  divider: {
    margin: '0 7px',
    height: 10,
    width: 1,
    backgroundColor: '#E9ECED',
  },
  link: {
    textDecoration: 'none',
  },
  icon: {
    marginRight: '5.5px',
  },
  grayscale: {
    filter: 'grayscale(1)',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    borderRadius: 8,
    overflow: 'hidden',
  },
  imageContainer: {
    flex: 1,
    overflow: 'hidden',
  },
  articleContainer: {
    padding: '12px 20px',
    borderBottom: '1px solid #E9ECED',
    borderLeft: '1px solid #E9ECED',
    borderRight: '1px solid #E9ECED',
    borderRadius: 8,
  },
  title: {
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    height: 40 /* Fallback for non-webkit */,
    marginBottom: '32px',
  },
  topBlogTitle: {
    marginBottom: 0,
  },
});

const BlogItem = React.memo(props => {
  const { classes, fields, grayscale, isTopBlog, trackingProps = {} } = props;
  const subtitleId = 'main-blog-item';
  const subtitleContainerHeight = useAlterSubtitleHeight(subtitleId, {
    baseHeight: 17,
    alterHeight: 33,
  });
  const locale = useAlgoliaLocale();
  const [siteCopy] = useSiteCopySelector(['resource-item-page']);
  const file = getImageFile(fields);
  const hasPublishDate =
    !_isEmpty(_get(fields, 'publicationDate')) ||
    typeof _get(fields, 'publicationDate') === 'number';
  const hasEditor = !_isEmpty(_get(fields, 'editors.0.fields'));
  const hasPerson = !_isEmpty(_get(fields, 'person.0.fields'));
  const blogType =
    hasEditor && hasPerson
      ? 'Guest'
      : hasEditor
      ? 'CredibleMind'
      : hasPerson
      ? 'Guest'
      : null;
  let blogAuthor;
  const isGuest = blogType === 'Guest';
  if (blogType) {
    blogAuthor = isGuest
      ? getAlgoliaLocalizedField(fields, 'person', locale, [])
      : getAlgoliaLocalizedField(fields, 'editors', locale, []);
  }
  const dateFormatFilter = useDateLocale();
  const formattedDate =
    hasPublishDate &&
    format(
      typeof fields.publicationDate === 'number'
        ? fields.publicationDate
        : parseISO(fields.publicationDate),
      'MMM dd, yyyy',
      {
        locale: dateFormatFilter,
      },
    );

  const redirect = item => {
    const pageName = window.location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track('Resource Card - Clicked', {
      name: item.name,
      type: 'article',
      subtype: item.type,
      blog: true,
      page: finalPageName,
      path: window.location.pathname.split('/')[1],
      slug: window.location.pathname.split('/')[2],
      entryId: item.objectID,
      ...trackingProps,
    });
  };

  return (
    <Link
      to={`/insights/${fields.slug}`}
      onClick={() => redirect(fields)}
      className={classes.link}
    >
      <Box className={classes.container}>
        <Box className={classes.imageContainer}>
          <LazyLoadImage
            src={file}
            width="100%"
            height="100%"
            data-src={file}
            alt="CredibleMind Blog"
            onError={e => {
              e.target.src = fallbackImage;
            }}
            className={_classNames(classes.lazyImage, {
              [classes.grayscale]: grayscale,
            })}
          />
        </Box>
        <Box className={_classNames(classes.articleContainer)}>
          <Typography
            variant="overline"
            color="textSecondary"
            align="left"
            className={classes.typeLabel}
          >
            <img src={BlogIcon} alt="blog icon" className={classes.icon} />
            {_get(
              siteCopy,
              ['pageCopy', 'resourceCardTypeMapping', 'Blog'],
              'Blog',
            )}
          </Typography>
          <Typography
            align="left"
            variant="h6"
            className={_classNames(classes.title, {
              [classes.topBlogTitle]: isTopBlog,
            })}
            color="textPrimary"
          >
            {getAlgoliaLocalizedField(fields, 'name', locale)}
          </Typography>
          <div className={_classNames(classes.dateAndAuthorWrapper)}>
            <Typography align="left" className={classes.date}>
              {formattedDate}
            </Typography>
            {!_isEmpty(blogAuthor) && (
              <>
                <div className={classes.divider} />
                <Typography
                  align="left"
                  className={classes.author}
                  component="span"
                >
                  <LabelValue
                    type={isGuest ? 'people' : 'editors'}
                    value={blogAuthor}
                  />
                </Typography>
              </>
            )}
          </div>
          <Box style={{ height: `${subtitleContainerHeight}px` }}>
            <AuthorSubtitle data={blogAuthor} id={subtitleId} />
          </Box>
        </Box>
      </Box>
    </Link>
  );
});

export default withStyles(styles)(BlogItem);
